'use client'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Clock } from 'lucide-react'
import { useMemo } from 'react'
import type { RepeatSpec } from 'sprites-types/api/v2/scheduled-tasks'

/** Get user's current IANA timezone (e.g. America/New_York) */
export function getUserTimezone(): string {
  if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) {
    return 'UTC'
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC'
}

/**
 * Convert a local hour (0–23) and local days-of-week to their UTC equivalents.
 * The displayed local time is localHour:displayMinute (where displayMinute = |offsetMin| % 60).
 * Converts that exact local time to UTC, yielding a whole UTC hour and any day-boundary shift.
 */
export function localHourAndDaysToUtc(
  localHour: number,
  localDays: number[],
  timezone: string
): { utcHour: number; utcDays: number[] } {
  const offsetMin = getTimezoneOffsetMinutes(timezone, new Date())
  // Include the sub-hour display minute so e.g. "9:30 AM IST" → 4:00 UTC, not 3:00 UTC.
  // utcTotalMinutes is always an exact multiple of 60 (|offsetMin|%60 + offsetMin cancels the remainder).
  const displayMinute = Math.abs(offsetMin) % 60
  const utcTotalMinutes = localHour * 60 + displayMinute + offsetMin
  const utcHour = ((Math.round(utcTotalMinutes / 60) % 24) + 24) % 24
  const dayShift = Math.floor(utcTotalMinutes / (60 * 24))
  const utcDays = localDays.map((d) => (((d + dayShift) % 7) + 7) % 7)
  return { utcHour, utcDays }
}

/**
 * Convert a UTC hour (0–23) and UTC days-of-week back to their local equivalents for display.
 * This is the inverse of localHourAndDaysToUtc.
 */
export function utcHourAndDaysToLocal(
  utcHour: number,
  utcDays: number[],
  timezone: string
): { localHour: number; localDays: number[] } {
  const offsetMin = getTimezoneOffsetMinutes(timezone, new Date())
  // Recover the original local hour
  const rawLocalHour = utcHour - offsetMin / 60
  const localHour = ((Math.floor(rawLocalHour) % 24) + 24) % 24
  // Recover the dayShift that was applied in the forward direction
  const rawUtcHour = localHour + offsetMin / 60
  const dayShift = Math.floor(rawUtcHour / 24)
  const localDays = utcDays.map((d) => (((d - dayShift) % 7) + 7) % 7)
  return { localHour, localDays }
}

/**
 * UTC offset in minutes for timezone tz at the given instant.
 * Positive = zone is behind UTC (e.g. New York UTC-5 → +300).
 */
function getTimezoneOffsetMinutes(tz: string, date: Date): number {
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
    })
    // Truncate to minute precision so sub-minute seconds don't cause Math.round to drift by 1
    const d = new Date(Math.floor(date.getTime() / 60000) * 60000)
    const parts = formatter.formatToParts(d)
    const get = (type: string) => parts.find((p) => p.type === type)?.value
    const year = parseInt(get('year') ?? '0', 10)
    const month = parseInt(get('month') ?? '1', 10) - 1
    const day = parseInt(get('day') ?? '1', 10)
    const hour = parseInt(get('hour') ?? '0', 10)
    const minute = parseInt(get('minute') ?? '0', 10)
    const localAsUtcMs = Date.UTC(year, month, day, hour, minute)
    return (d.getTime() - localAsUtcMs) / 60000
  } catch {
    return 0
  }
}

/** Full list of IANA timezones, sorted by UTC offset (UTC-12 first). */
export function getTimezonesForSelector(include?: string): string[] {
  let zones: string[]
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      zones = Intl.supportedValuesOf('timeZone')
    } else {
      zones = ['UTC']
    }
  } catch {
    zones = ['UTC']
  }
  const set = new Set(zones)
  if (include && !set.has(include)) set.add(include)
  const list = Array.from(set)
  const now = new Date()
  return list.sort((a, b) => {
    const offsetA = getTimezoneOffsetMinutes(a, now)
    const offsetB = getTimezoneOffsetMinutes(b, now)
    return offsetB - offsetA
  })
}

/**
 * Minute (0-59) that aligns with whole-hour UTC in this timezone.
 * E.g. GMT+10:30 → 30; GMT+5:45 → 45; GMT-5 → 0.
 */
export function getDisplayMinuteForTimezone(tz: string, date: Date): number {
  const offsetMin = getTimezoneOffsetMinutes(tz, date)
  return Math.abs(offsetMin) % 60
}

/** Format hour (0-23) and optional minute (0-59) for display (e.g. "9:00 AM" or "9:30 AM"). */
export function formatHourForDisplay(hour: number, minute: number = 0): string {
  const h = hour % 24
  const min = minute % 60
  const minStr = min === 0 ? '00' : String(min).padStart(2, '0')
  if (h === 0) return `12:${minStr} AM`
  if (h === 12) return `12:${minStr} PM`
  if (h < 12) return `${h}:${minStr} AM`
  return `${h - 12}:${minStr} PM`
}

/** Format timezone for display: friendly name + offset (e.g. "New York (UTC-5)") */
export function formatTimezoneLabel(tz: string): { offset: string; name: string } {
  const now = new Date()
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  })
  const parts = formatter.formatToParts(now)
  const offsetPart = parts.find((p) => p.type === 'timeZoneName')
  const offset = offsetPart?.value ?? ''
  const name = tz.replace(/_/g, ' ').replace(/\//g, ' / ')
  return { offset, name }
}

/** Short timezone abbreviation for display (e.g. "PST", "GMT+5:30") */
export function getShortTimezoneAbbr(tz: string, date: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(date)
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

// Helper to get ordinal suffix (1st, 2nd, 3rd, etc.)
export function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

// UI preset type - just shortcuts for common patterns
export type SchedulePreset = 'never' | 'daily' | 'weekly' | 'custom'

// Interval unit for custom schedules
export type IntervalUnit = 'day' | 'week' | 'month'

/** 1=1st, 2=2nd, 3=3rd, 4=4th, 5=last. weekday 0-6 (Sun-Sat). */
export type MonthWeekdayRule = { which: number; weekday: number }

export interface ScheduleEditorProps {
  // Date/time settings
  startDate: string
  startHour: number
  timezone: string
  // Schedule configuration
  preset: SchedulePreset
  interval: number
  intervalUnit: IntervalUnit
  daysOfWeek: number[]
  monthDayOfMonth: number | null
  monthWeekdayRule: MonthWeekdayRule | null
  // End conditions
  maxRuns: number | null
  endMode: 'none' | 'maxRuns' | 'endDate'
  endDate: string
  // Callbacks
  onStartDateChange: (date: string) => void
  onStartHourChange: (hour: number) => void
  onTimezoneChange: (timezone: string) => void
  onPresetChange: (preset: SchedulePreset) => void
  onIntervalChange: (interval: number) => void
  onIntervalUnitChange: (unit: IntervalUnit) => void
  onDaysOfWeekChange: (days: number[]) => void
  onMonthDayOfMonthChange: (day: number | null) => void
  onMonthWeekdayRuleChange: (rule: MonthWeekdayRule | null) => void
  onMaxRunsChange: (runs: number | null) => void
  onEndModeChange: (mode: 'none' | 'maxRuns' | 'endDate') => void
  onEndDateChange: (date: string) => void
}

/**
 * Build API RepeatSpec from schedule editor state.
 * startHour and daysOfWeek are LOCAL to the given timezone and are converted to UTC before
 * being stored in the RepeatSpec. Returns null for one-time tasks (preset === 'never').
 */
export function buildRepeatSpec(params: {
  preset: SchedulePreset
  startHour: number
  interval: number
  intervalUnit: IntervalUnit
  daysOfWeek: number[]
  monthDayOfMonth: number | null
  monthWeekdayRule: MonthWeekdayRule | null
  timezone?: string
}): RepeatSpec | null {
  const {
    preset,
    startHour: localHour,
    interval,
    intervalUnit,
    daysOfWeek: localDays,
    monthDayOfMonth,
    monthWeekdayRule,
    timezone,
  } = params

  // Convert local hour/days to UTC
  const { utcHour, utcDays } = timezone
    ? localHourAndDaysToUtc(localHour, localDays, timezone)
    : { utcHour: localHour, utcDays: localDays }

  // One-time task = no repeatSpec
  if (preset === 'never') {
    return null
  }

  // Daily preset
  if (preset === 'daily') {
    return { kind: 'daily', startHour: utcHour }
  }

  // Weekly preset (always interval=1)
  if (preset === 'weekly') {
    return {
      kind: 'weekly',
      startHour: utcHour,
      interval: 1,
      daysOfWeek: utcDays.length > 0 ? [...utcDays].sort((a, b) => a - b) : [],
    }
  }

  // Custom preset - depends on intervalUnit
  if (intervalUnit === 'day') {
    if (interval === 1) {
      return { kind: 'daily', startHour: utcHour }
    }
    return { kind: 'every_x_days', startHour: utcHour, interval }
  }

  if (intervalUnit === 'week') {
    return {
      kind: 'weekly',
      startHour: utcHour,
      interval: interval >= 1 ? interval : 1,
      daysOfWeek: utcDays.length > 0 ? [...utcDays].sort((a, b) => a - b) : [],
    }
  }

  if (intervalUnit === 'month') {
    const spec: RepeatSpec = {
      kind: 'monthly',
      startHour: utcHour,
      interval: interval >= 1 ? interval : 1,
    }
    if (monthWeekdayRule) {
      spec.monthlyOn = {
        type: 'nthWeekday',
        monthWeekday: {
          which: monthWeekdayRule.which,
          weekday: monthWeekdayRule.weekday,
        },
      }
    } else if (monthDayOfMonth != null) {
      spec.monthlyOn = { type: 'dayOfMonth', day: monthDayOfMonth }
    }
    return spec
  }

  return null
}

/**
 * Convert RepeatSpec to editor state for prefilling.
 * repeatSpec.startHour and daysOfWeek are UTC; if timezone is provided they are converted
 * to local time for display. Returns the appropriate preset and configuration.
 */
export function repeatSpecToEditorState(
  repeatSpec: RepeatSpec | null | undefined,
  timezone?: string
): {
  preset: SchedulePreset
  interval: number
  intervalUnit: IntervalUnit
  startHour: number
  daysOfWeek: number[]
  monthDayOfMonth: number | null
  monthWeekdayRule: MonthWeekdayRule | null
} {
  // No repeatSpec = one-time task
  if (!repeatSpec) {
    return {
      preset: 'never',
      interval: 1,
      intervalUnit: 'day',
      startHour: 9,
      daysOfWeek: [],
      monthDayOfMonth: null,
      monthWeekdayRule: null,
    }
  }

  const utcHour = repeatSpec.startHour ?? 9
  const utcDays = repeatSpec.daysOfWeek ? [...repeatSpec.daysOfWeek] : []

  // Convert UTC hour/days to local for display
  const { localHour: startHour, localDays } = timezone
    ? utcHourAndDaysToLocal(utcHour, utcDays, timezone)
    : { localHour: utcHour, localDays: utcDays }

  // Daily
  if (repeatSpec.kind === 'daily') {
    return {
      preset: 'daily',
      interval: 1,
      intervalUnit: 'day',
      startHour,
      daysOfWeek: [],
      monthDayOfMonth: null,
      monthWeekdayRule: null,
    }
  }

  // Every X days
  if (repeatSpec.kind === 'every_x_days') {
    return {
      preset: 'custom',
      interval: repeatSpec.interval ?? 1,
      intervalUnit: 'day',
      startHour,
      daysOfWeek: [],
      monthDayOfMonth: null,
      monthWeekdayRule: null,
    }
  }

  // Weekly
  if (repeatSpec.kind === 'weekly') {
    const interval = repeatSpec.interval ?? 1

    // Simple weekly (interval=1) uses the Weekly preset
    // Multi-week interval uses Custom preset
    return {
      preset: interval === 1 ? 'weekly' : 'custom',
      interval,
      intervalUnit: 'week',
      startHour,
      daysOfWeek: localDays,
      monthDayOfMonth: null,
      monthWeekdayRule: null,
    }
  }

  // Monthly
  if (repeatSpec.kind === 'monthly') {
    const monthlyOn = repeatSpec.monthlyOn
    const monthDayOfMonth = monthlyOn?.type === 'dayOfMonth' ? monthlyOn.day : null
    const monthWeekdayRule =
      monthlyOn?.type === 'nthWeekday'
        ? {
            which: monthlyOn.monthWeekday.which,
            weekday: monthlyOn.monthWeekday.weekday,
          }
        : null

    return {
      preset: 'custom',
      interval: repeatSpec.interval ?? 1,
      intervalUnit: 'month',
      startHour,
      daysOfWeek: [],
      monthDayOfMonth,
      monthWeekdayRule,
    }
  }

  // Fallback
  return {
    preset: 'never',
    interval: 1,
    intervalUnit: 'day',
    startHour: 9,
    daysOfWeek: [],
    monthDayOfMonth: null,
    monthWeekdayRule: null,
  }
}

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i)

const MONTH_ORDINALS: { value: 1 | 2 | 3 | 4 | 5; label: string }[] = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: 5, label: 'Last' },
]

/**
 * UTC ISO string for "startDate at startHour" in the given timezone.
 * Used when creating a scheduled task so the first run is at the user-selected date/time.
 */
export function getFirstRunAtISO(startDate: string, startHour: number, timezone: string): string {
  const [y, m, d] = startDate.split('-').map(Number)
  const probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  const offsetMin = getTimezoneOffsetMinutes(timezone, probe)
  const utcMs = Date.UTC(y, m - 1, d, startHour, 0, 0) + offsetMin * 60 * 1000
  return new Date(utcMs).toISOString()
}

/** Number of days in the month of the given date (1–31). */
function getDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
}

/**
 * The occurrence of the weekday on the given date (1st–4th or 5=Last).
 */
function getMonthWeekdayRuleForDate(date: Date): MonthWeekdayRule {
  const dayOfMonth = date.getDate()
  const weekday = date.getDay()
  const year = date.getFullYear()
  const month = date.getMonth()
  const daysInMonth = getDaysInMonth(date)
  let occurrence = 0
  for (let d = 1; d <= dayOfMonth; d += 1) {
    if (new Date(year, month, d).getDay() === weekday) occurrence += 1
  }
  let totalInMonth = 0
  for (let d = 1; d <= daysInMonth; d += 1) {
    if (new Date(year, month, d).getDay() === weekday) totalInMonth += 1
  }
  const which: 1 | 2 | 3 | 4 | 5 = occurrence === totalInMonth ? 5 : (occurrence as 1 | 2 | 3 | 4)
  return { which, weekday }
}

export function ScheduleEditor({
  startDate,
  startHour,
  timezone,
  preset,
  interval,
  intervalUnit,
  daysOfWeek,
  monthDayOfMonth,
  monthWeekdayRule,
  maxRuns,
  endMode,
  endDate,
  onStartDateChange,
  onStartHourChange,
  onTimezoneChange,
  onPresetChange,
  onIntervalChange,
  onIntervalUnitChange,
  onDaysOfWeekChange,
  onMonthDayOfMonthChange,
  onMonthWeekdayRuleChange,
  onMaxRunsChange,
  onEndModeChange,
  onEndDateChange,
}: ScheduleEditorProps) {
  const startDateObj = useMemo(() => {
    const [year, month, day] = startDate.split('-').map(Number)
    return new Date(year, month - 1, day)
  }, [startDate])

  const defaultDayOfWeek = startDateObj.getDay()
  const effectiveDaysOfWeek = daysOfWeek.length > 0 ? daysOfWeek : [defaultDayOfWeek]

  const displayMinute = useMemo(
    () => getDisplayMinuteForTimezone(timezone, startDateObj),
    [timezone, startDateObj]
  )

  const timezones = useMemo(() => getTimezonesForSelector(timezone), [timezone])

  const unitLabel = (u: IntervalUnit, n: number) =>
    u === 'day'
      ? n === 1
        ? 'day'
        : 'days'
      : u === 'week'
        ? n === 1
          ? 'week'
          : 'weeks'
        : n === 1
          ? 'month'
          : 'months'

  // Is this a recurring task?
  const isRecurring = preset !== 'never'

  return (
    <div className="space-y-4">
      {/* Start Date, Time, Timezone */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-500">Starting on</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-900 focus:border-gray-400 focus:outline-none [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100"
            style={{ colorScheme: 'light' }}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-500">
            <div className="flex items-center gap-1.5">
              <Clock size={14} />
              At
            </div>
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="font-space-grotesk flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-2 text-left text-sm font-medium text-gray-900 hover:border-gray-300 focus:border-gray-400 focus:outline-none"
              >
                <span>{formatHourForDisplay(startHour, displayMinute)}</span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="font-space-grotesk z-[10000] max-h-60 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto border-gray-200 bg-white text-gray-900 [&_[data-highlighted]]:bg-gray-100 [&_[data-highlighted]]:text-gray-900"
            >
              {HOUR_OPTIONS.map((h) => (
                <DropdownMenuItem key={h} onSelect={() => onStartHourChange(h)}>
                  {formatHourForDisplay(h, displayMinute)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-500">Timezone</label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="font-space-grotesk flex w-full items-center gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-left text-sm font-medium text-gray-900 hover:border-gray-300 focus:border-gray-400 focus:outline-none"
            >
              <span className="truncate opacity-50">{formatTimezoneLabel(timezone).offset}</span>
              <span className="truncate">{formatTimezoneLabel(timezone).name}</span>
              <ChevronDown className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="font-space-grotesk z-[10000] max-h-[min(60vh,320px)] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto border-gray-200 bg-white text-gray-900 [&_[data-highlighted]]:bg-gray-100 [&_[data-highlighted]]:text-gray-900"
          >
            {timezones.map((tz) => (
              <DropdownMenuItem key={tz} onSelect={() => onTimezoneChange(tz)}>
                <span className="truncate opacity-50">{formatTimezoneLabel(tz).offset}</span>
                <span className="truncate">{formatTimezoneLabel(tz).name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Repeat: Never | Daily | Weekly | Custom */}
      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-500">Repeat</label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="font-space-grotesk flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-2 text-left text-sm font-medium text-gray-900 hover:border-gray-300 focus:border-gray-400 focus:outline-none"
            >
              <span>
                {preset === 'never'
                  ? 'Never'
                  : preset === 'daily'
                    ? 'Daily'
                    : preset === 'weekly'
                      ? 'Weekly'
                      : 'Custom'}
              </span>
              <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="font-space-grotesk z-[10000] border-gray-200 bg-white text-gray-900 [&_[data-highlighted]]:bg-gray-100 [&_[data-highlighted]]:text-gray-900"
          >
            <DropdownMenuItem onSelect={() => onPresetChange('never')}>Never</DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onPresetChange('daily')
                onIntervalChange(1)
                onIntervalUnitChange('day')
              }}
            >
              Daily
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onPresetChange('weekly')
                onIntervalChange(1)
                onIntervalUnitChange('week')
                if (daysOfWeek.length === 0) {
                  onDaysOfWeekChange([defaultDayOfWeek])
                }
              }}
            >
              Weekly
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                onPresetChange('custom')
                // Default to every 2 days when switching to custom
                if (intervalUnit === 'day' && interval === 1) {
                  onIntervalChange(2)
                }
              }}
            >
              Custom
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Weekly: day-of-week selector */}
      {preset === 'weekly' && (
        <div>
          <span className="mb-1.5 block text-xs text-gray-500">On</span>
          <div className="flex flex-wrap gap-1.5">
            {weekDays.map((day, index) => {
              const selected = effectiveDaysOfWeek.includes(index)
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    if (selected) {
                      if (effectiveDaysOfWeek.length > 1) {
                        onDaysOfWeekChange(effectiveDaysOfWeek.filter((d) => d !== index))
                      }
                    } else {
                      onDaysOfWeekChange(
                        [...effectiveDaysOfWeek.filter((d) => d !== index), index].sort(
                          (a, b) => a - b
                        )
                      )
                    }
                  }}
                  className={cn(
                    'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                    selected
                      ? 'bg-gray-200 text-gray-900'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {day.slice(0, 3)}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Custom: Every [number] [unit] */}
      {preset === 'custom' && (
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-500">How often?</label>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Every</span>
            <input
              type="number"
              min={1}
              value={interval}
              onChange={(e) => {
                const raw = e.target.value
                const val = raw === '' ? 1 : parseInt(raw, 10)
                onIntervalChange(Number.isNaN(val) || val < 1 ? 1 : val)
              }}
              className="w-16 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-center text-sm font-medium text-gray-900 focus:border-gray-400 focus:outline-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="font-space-grotesk flex items-center gap-1 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-900 hover:border-gray-300 focus:border-gray-400 focus:outline-none"
                >
                  <span>{unitLabel(intervalUnit, interval)}</span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="font-space-grotesk z-[10000] border-gray-200 bg-white text-gray-900 [&_[data-highlighted]]:bg-gray-100 [&_[data-highlighted]]:text-gray-900"
              >
                {(['day', 'week', 'month'] as const).map((u) => (
                  <DropdownMenuItem
                    key={u}
                    onSelect={() => {
                      onIntervalUnitChange(u)
                      if (u === 'week' && daysOfWeek.length === 0) {
                        onDaysOfWeekChange([defaultDayOfWeek])
                      }
                      if (u === 'month' && monthDayOfMonth == null && monthWeekdayRule == null) {
                        onMonthDayOfMonthChange(startDateObj.getDate())
                      }
                    }}
                  >
                    {u === 'day' ? 'day(s)' : u === 'week' ? 'week(s)' : 'month(s)'}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Weekly options in custom mode */}
          {intervalUnit === 'week' && (
            <div className="mt-2">
              <span className="mb-1.5 block text-xs text-gray-500">On</span>
              <div className="flex flex-wrap gap-1.5">
                {weekDays.map((day, index) => {
                  const selected = effectiveDaysOfWeek.includes(index)
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        if (selected) {
                          if (effectiveDaysOfWeek.length > 1) {
                            onDaysOfWeekChange(effectiveDaysOfWeek.filter((d) => d !== index))
                          }
                        } else {
                          onDaysOfWeekChange(
                            [...effectiveDaysOfWeek.filter((d) => d !== index), index].sort(
                              (a, b) => a - b
                            )
                          )
                        }
                      }}
                      className={cn(
                        'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                        selected
                          ? 'bg-gray-200 text-gray-900'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {day.slice(0, 3)}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Monthly options in custom mode */}
          {intervalUnit === 'month' && (
            <div className="mt-2 space-y-2">
              <span className="block text-xs text-gray-500">On</span>
              <div className="flex flex-wrap items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'font-space-grotesk rounded-2xl border px-3 py-2 text-sm font-medium',
                        monthWeekdayRule
                          ? 'border-gray-200 bg-gray-100 text-gray-900'
                          : 'border-gray-200 bg-white text-gray-900'
                      )}
                    >
                      {monthWeekdayRule
                        ? `${MONTH_ORDINALS.find((o) => o.value === monthWeekdayRule.which)?.label ?? ''} ${weekDays[monthWeekdayRule.weekday]}`
                        : `Day ${Math.min(monthDayOfMonth ?? startDateObj.getDate(), getDaysInMonth(startDateObj))}`}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="font-space-grotesk z-[10000] max-h-60 overflow-y-auto border-gray-200 bg-white text-gray-900 [&_[data-highlighted]]:bg-gray-100 [&_[data-highlighted]]:text-gray-900"
                  >
                    {(() => {
                      const dayOfMonth = startDateObj.getDate()
                      const startWeekdayRule = getMonthWeekdayRuleForDate(startDateObj)
                      const ordLabel =
                        MONTH_ORDINALS.find((o) => o.value === startWeekdayRule.which)?.label ?? ''
                      return (
                        <>
                          <DropdownMenuItem
                            key={dayOfMonth}
                            onSelect={() => {
                              onMonthDayOfMonthChange(dayOfMonth)
                              onMonthWeekdayRuleChange(null)
                            }}
                          >
                            Day {dayOfMonth}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => {
                              onMonthWeekdayRuleChange(startWeekdayRule)
                              onMonthDayOfMonthChange(null)
                            }}
                          >
                            {ordLabel} {weekDays[startWeekdayRule.weekday]}
                          </DropdownMenuItem>
                        </>
                      )
                    })()}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Summary */}
      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <p className="text-xs font-medium text-gray-700">Summary</p>
        <p className="mt-1 text-sm text-gray-600">
          {preset === 'never' ? (
            <>
              <span className="font-semibold text-gray-900">
                {new Date(startDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>{' '}
              at{' '}
              <span className="font-semibold text-gray-900">
                {formatHourForDisplay(startHour, displayMinute)}
              </span>
            </>
          ) : preset === 'daily' ? (
            <>
              Every day at{' '}
              <span className="font-semibold text-gray-900">
                {formatHourForDisplay(startHour, displayMinute)}
              </span>
            </>
          ) : preset === 'weekly' ? (
            <>
              Every{' '}
              <span className="font-semibold text-gray-900">
                {effectiveDaysOfWeek.map((d) => weekDays[d]).join(', ')}
              </span>{' '}
              at{' '}
              <span className="font-semibold text-gray-900">
                {formatHourForDisplay(startHour, displayMinute)}
              </span>
            </>
          ) : intervalUnit === 'day' ? (
            interval === 1 ? (
              <>
                Every day at{' '}
                <span className="font-semibold text-gray-900">
                  {formatHourForDisplay(startHour, displayMinute)}
                </span>
              </>
            ) : (
              <>
                Every <span className="font-semibold text-gray-900">{interval}</span> days at{' '}
                <span className="font-semibold text-gray-900">
                  {formatHourForDisplay(startHour, displayMinute)}
                </span>
              </>
            )
          ) : intervalUnit === 'week' ? (
            <>
              Every{' '}
              {interval > 1 && <span className="font-semibold text-gray-900">{interval} </span>}
              {interval > 1 ? 'weeks' : 'week'} on{' '}
              <span className="font-semibold text-gray-900">
                {effectiveDaysOfWeek.map((d) => weekDays[d]).join(', ')}
              </span>{' '}
              at{' '}
              <span className="font-semibold text-gray-900">
                {formatHourForDisplay(startHour, displayMinute)}
              </span>
            </>
          ) : intervalUnit === 'month' && monthWeekdayRule ? (
            <>
              Every{' '}
              {interval > 1 && (
                <span className="font-semibold text-gray-900">{interval} months, </span>
              )}
              on the{' '}
              <span className="font-semibold text-gray-900">
                {MONTH_ORDINALS.find((o) => o.value === monthWeekdayRule.which)?.label ?? ''}{' '}
                {weekDays[monthWeekdayRule.weekday]}
              </span>{' '}
              at{' '}
              <span className="font-semibold text-gray-900">
                {formatHourForDisplay(startHour, displayMinute)}
              </span>
            </>
          ) : intervalUnit === 'month' ? (
            <>
              Every{' '}
              {interval > 1 && (
                <span className="font-semibold text-gray-900">{interval} months, </span>
              )}
              on the{' '}
              <span className="font-semibold text-gray-900">
                {Math.min(monthDayOfMonth ?? startDateObj.getDate(), getDaysInMonth(startDateObj))}
                {getOrdinalSuffix(
                  Math.min(monthDayOfMonth ?? startDateObj.getDate(), getDaysInMonth(startDateObj))
                )}
              </span>{' '}
              at{' '}
              <span className="font-semibold text-gray-900">
                {formatHourForDisplay(startHour, displayMinute)}
              </span>
            </>
          ) : null}
        </p>
      </div>

      {/* End condition - only show for recurring tasks */}
      <AnimatePresence mode="wait">
        {isRecurring && (
          <motion.div
            key="end-condition"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="space-y-2 overflow-hidden"
          >
            <label className="mb-1.5 block text-xs font-medium text-gray-500">
              When should it stop?
            </label>
            <div className="space-y-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="font-space-grotesk flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-2 text-left text-sm font-medium text-gray-900 hover:border-gray-300 focus:border-gray-400 focus:outline-none"
                  >
                    <span>
                      {endMode === 'none'
                        ? 'Never'
                        : endMode === 'maxRuns'
                          ? 'After X times'
                          : 'On a specific date'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="font-space-grotesk z-[10000] w-[var(--radix-dropdown-menu-trigger-width)] border-gray-200 bg-white text-gray-900 [&_[data-highlighted]]:bg-gray-100 [&_[data-highlighted]]:text-gray-900"
                >
                  <DropdownMenuItem
                    onSelect={() => {
                      onEndModeChange('none')
                      onMaxRunsChange(null)
                      onEndDateChange('')
                    }}
                  >
                    Never
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      onEndModeChange('maxRuns')
                      onEndDateChange('')
                    }}
                  >
                    After X times
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      onEndModeChange('endDate')
                      onMaxRunsChange(null)
                    }}
                  >
                    On a specific date
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {endMode === 'maxRuns' && (
                <div className="flex items-center gap-2 pl-1">
                  <span className="text-sm text-gray-600">Stop after</span>
                  <input
                    type="number"
                    value={maxRuns || ''}
                    onChange={(e) =>
                      onMaxRunsChange(e.target.value ? parseInt(e.target.value, 10) : null)
                    }
                    placeholder="5"
                    min={1}
                    className="w-20 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-center text-sm text-gray-900 focus:border-gray-400 focus:outline-none"
                  />
                  <span className="text-sm text-gray-600">times</span>
                </div>
              )}
              {endMode === 'endDate' && (
                <div className="flex items-center gap-2 pl-1">
                  <span className="text-sm text-gray-600">Stop on</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    min={startDate}
                    className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-gray-400 focus:outline-none [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    style={{ colorScheme: 'light' }}
                  />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
